/**
 * Get CSS variable value
 */
export function getCSSVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Parse color string to RGB components
 */
export function parseColor(color: string): { r: number; g: number; b: number } {
  // Handle RGB format: rgb(r, g, b)
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    return { r: parseInt(rgbMatch[1]), g: parseInt(rgbMatch[2]), b: parseInt(rgbMatch[3]) };
  }
  
  // Handle hex format: #RRGGBB
  const hexMatch = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (hexMatch) {
    return {
      r: parseInt(hexMatch[1], 16),
      g: parseInt(hexMatch[2], 16),
      b: parseInt(hexMatch[3], 16)
    };
  }
  
  // Default fallback
  return { r: 240, g: 240, b: 240 };
}

/**
 * Base Visualizer class with common functionality
 */
export abstract class BaseVisualizer {
  protected canvas: HTMLCanvasElement | null = null;
  protected ctx: CanvasRenderingContext2D | null = null;
  protected offscreenCanvas: OffscreenCanvas | null = null;
  protected offscreenCtx: OffscreenCanvasRenderingContext2D | null = null;
  protected animationId: number | null = null;
  protected isActive = false;

  // Monochrome color scheme from CSS variables
  protected colors = {
    background: '#0a0a0a',
    primary: '#f0f0f0',
    secondary: 'rgba(240, 240, 240, 0.5)',
  };

  abstract init(containerId: string): void;
  protected abstract draw(): void;

  protected createCanvas(container: HTMLElement): void {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'visualizer-canvas';
    container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');
    
    // Update colors from CSS
    this.colors = {
      background: getCSSVar('--color-background') || '#0a0a0a',
      primary: getCSSVar('--color-foreground') || '#f0f0f0',
      secondary: getCSSVar('--color-foreground-muted') || 'rgba(240, 240, 240, 0.5)',
    };
  }

  protected resize(): void {
    if (!this.canvas) return;
    const rect = this.canvas.parentElement?.getBoundingClientRect();
    if (!rect) return;
    
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;
    
    if (this.ctx) {
      this.ctx.scale(dpr, dpr);
    }
  }

  protected getCanvasSize(): { width: number; height: number } {
    if (!this.canvas) return { width: 0, height: 0 };
    const rect = this.canvas.parentElement?.getBoundingClientRect();
    return { width: rect?.width || 0, height: rect?.height || 0 };
  }

  protected applyOpacity(color: string, opacity: number): string {
    const { r, g, b } = parseColor(color);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  /**
   * Update colors from CSS variables (call when theme changes)
   */
  updateColors(): void {
    this.colors = {
      background: getCSSVar('--color-background') || '#0a0a0a',
      primary: getCSSVar('--color-foreground') || '#f0f0f0',
      secondary: getCSSVar('--color-foreground-muted') || 'rgba(240, 240, 240, 0.5)',
    };
  }

  start(): void {
    this.isActive = true;
    this.animate();
  }

  protected animate = (): void => {
    if (!this.isActive) return;
    this.draw();
    this.animationId = requestAnimationFrame(this.animate);
  };

  stop(): void {
    this.isActive = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  dispose(): void {
    this.stop();
    if (this.canvas && this.canvas.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }
    this.canvas = null;
    this.ctx = null;
    this.offscreenCanvas = null;
    this.offscreenCtx = null;
  }
}
